import React, { useMemo, useState } from 'react';
import { Globe, Search, Check, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useZakatGlobal } from '../../context/ZakatGlobalContext';
import { REGIONS, LANGUAGE_GROUPS, regionOf } from '../../config/countryGeography';

// Two-tab country picker: "By region" / "By language" + search.
// Replaces the basic <Select> trigger so the user can find their country
// in the way that feels most natural — geographically OR by language.
//
// Props:
//   variant  — 'desktop' | 'mobile-icon'
//   testid   — base test-id (suffix `-trigger` and `-item-{CODE}` are
//              automatically derived)
//
// Behaviour:
//   • Tab 1 ("Region"): filters countries by REGION_OF mapping
//   • Tab 2 ("Language"): filters by `country.languages.includes(...)`
//   • Search box is always active; matches name, native name, currency
//   • Selecting a country triggers `updateCountry(code)` + closes the popover.
//
// The component intentionally renders ALL filtered countries (~48) — they
// fit fine in a scrollable list.
export const CountryPicker = ({ variant = 'desktop', testid = 'country-picker' }) => {
  const { countries, selectedCountry, updateCountry } = useZakatGlobal();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('region'); // 'region' | 'lang'
  const [regionId, setRegionId] = useState('all');
  const [langId, setLangId] = useState('all');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesSearch = (c) =>
      !q ||
      c.name.toLowerCase().includes(q) ||
      (c.nameNative || '').toLowerCase().includes(q) ||
      (c.currency || '').toLowerCase().includes(q) ||
      c.code.toLowerCase() === q;

    if (tab === 'lang') {
      const group = LANGUAGE_GROUPS.find((g) => g.id === langId) || LANGUAGE_GROUPS[0];
      return countries.filter((c) => group.matches(c) && matchesSearch(c));
    }
    if (regionId === 'all') return countries.filter(matchesSearch);
    return countries.filter((c) => regionOf(c.code) === regionId && matchesSearch(c));
  }, [countries, tab, regionId, langId, query]);

  const choose = (code) => {
    updateCountry(code);
    setOpen(false);
    setQuery('');
  };

  // -------- Triggers ---------------------------------------------------
  const desktopTrigger = (
    <button
      data-testid={`${testid}-trigger`}
      className="inline-flex items-center justify-center gap-1.5 h-9 w-auto min-w-[86px] rounded-md bg-[#EDF5F1] border border-[#CBD2D9] text-[#0D1520] px-2.5 hover:bg-[#EDF5F1] transition-colors text-sm"
      aria-label={`Country: ${selectedCountry.name}`}
      title={selectedCountry.name}
    >
      <span className="text-base leading-none shrink-0">{selectedCountry.flag}</span>
      <span className="font-mono text-[11px] font-semibold tracking-wider uppercase text-[#0D1520]">
        {selectedCountry.code}
      </span>
      <ChevronDown className="w-3.5 h-3.5 text-[#0D4733] shrink-0" />
    </button>
  );

  const mobileTrigger = (
    <button
      data-testid={`${testid}-trigger`}
      className="w-10 h-10 p-0 bg-transparent border-none text-[#0D1520] hover:bg-[#EDF5F1] rounded-full flex items-center justify-center"
      aria-label="Choose country"
    >
      <span className="text-lg">{selectedCountry.flag}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{variant === 'mobile-icon' ? mobileTrigger : desktopTrigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[340px] sm:w-[380px] p-0 border-stone-200 shadow-xl"
        data-testid={`${testid}-panel`}
      >
        {/* Tabs */}
        <div className="flex border-b border-stone-200 bg-stone-50 rounded-t-md">
          <TabBtn active={tab === 'region'} onClick={() => setTab('region')} testid={`${testid}-tab-region`}>
            <Globe className="w-3.5 h-3.5" /> By region
          </TabBtn>
          <TabBtn active={tab === 'lang'} onClick={() => setTab('lang')} testid={`${testid}-tab-lang`}>
            <span className="font-arabic text-[15px]" aria-hidden>ع</span> By language
          </TabBtn>
        </div>

        {/* Filter pills */}
        <div className="px-3 pt-3 pb-2 flex flex-wrap gap-1 border-b border-stone-100">
          {(tab === 'region' ? REGIONS : LANGUAGE_GROUPS).map((opt) => {
            const active = tab === 'region' ? regionId === opt.id : langId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`${testid}-pill-${opt.id}`}
                onClick={() => (tab === 'region' ? setRegionId(opt.id) : setLangId(opt.id))}
                className={`text-[11.5px] px-2 py-1 rounded-full border transition-colors ${
                  active
                    ? 'bg-[#EDF5F1] text-[#0D4733] border-[#CBD2D9]'
                    : 'bg-white text-stone-700 border-stone-200 hover:border-[#CBD2D9]'
                }`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="px-3 py-2 border-b border-stone-100">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-stone-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country, currency, or code…"
              data-testid={`${testid}-search`}
              className="w-full pl-8 pr-2 py-2 text-sm rounded-md border border-stone-200 focus:outline-none focus:border-[#0D4733] focus:ring-1 focus:ring-[#E2DCD5]"
            />
          </div>
        </div>

        {/* List */}
        <div
          className="max-h-[320px] overflow-y-auto py-1"
          data-testid={`${testid}-list`}
        >
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-center text-xs text-stone-500">No countries match this filter.</p>
          ) : (
            filtered.map((c) => {
              const selected = c.code === selectedCountry.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  data-testid={`${testid}-item-${c.code}`}
                  onClick={() => choose(c.code)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                    selected ? 'bg-[#F3EFEA] text-[#0D1520]' : 'hover:bg-stone-50 text-stone-800'
                  }`}
                >
                  <span className="text-base shrink-0">{c.flag}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{c.name}</span>
                    {c.nameNative && c.nameNative !== c.name && (
                      <span className="block text-[11px] text-stone-500 truncate" dir="auto">{c.nameNative}</span>
                    )}
                  </span>
                  <span className="text-[10px] font-mono text-stone-400 uppercase shrink-0">{c.currency}</span>
                  {selected && <Check className="w-3.5 h-3.5 text-[#0D4733] shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {/* Footer note */}
        <div className="px-3 py-2 text-[10px] text-stone-500 border-t border-stone-100 bg-stone-50 rounded-b-md">
          {filtered.length} of {countries.length} countries
        </div>
      </PopoverContent>
    </Popover>
  );
};

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-[12px] font-semibold transition-colors ${
      active ? 'text-[#0D1520] bg-white' : 'text-stone-500 hover:text-[#0D1520]'
    }`}
  >
    {children}
  </button>
);

export default CountryPicker;
